<template>
  <div>
    <page-toolbar>
      <vonap-toolbar-label label="Show only Unsent">
        <v-switch
          v-model="showOnlyUnsent"
          :style="'display:inline-block;margin:-6px 0;width:61px;top: 4px;position:relative'"
        >
        </v-switch>
      </vonap-toolbar-label>
    </page-toolbar>
    <kendo-grid
      height="calc(100vh - 214px)"
      ref="sendingListGrid"
      :editable="!isReadOnly && 'inline'"
      :dataSource="dataSource"
      :columns="columns"
      :detail-init="detailInit"
      :filterable="true"
      :sortable="true"
      @databound="colorize"
      @cancel="colorize"
    ></kendo-grid>

    <send-undelivered-mail-form
      v-model="undeliveredMailFormOpen"
      :selected-operations="selectedOperations"
      :form-params="formParams"
      @success="onSuccessUndeliveredMailForm"
    />

    <undelivered-balance-preview
      v-model="undeliveredBalancePreviewVisible"
      :form-params="undeliveredBalancePreviewParams"
      @success="undeliveredBalancePreviewVisible = $event"
    />
  </div>
</template>

<script>
import {getDataItem} from '@/helpers/kendo-helpers'

const $ = kendo.jQuery
import security from '@/mixins/security'
import {mapGetters} from 'vuex'
import {checkboxColumn, jsonDropDownEditor, readOnlyEditor} from '@/helpers/kendo-editors'
import notification from '@/helpers/notification'
import PageToolbar from '@/components/PageToolbar'
import VonapToolbarLabel from '@/components/VonapToolbarLabel'
import SendUndeliveredMailForm from '@/components/SendUndeliveredMailForm'
import UndeliveredBalancePreview from '@/components/UndeliveredBalancePreview'

export default {
  name: 'SendingListGrid',
  components: {
    PageToolbar,
    VonapToolbarLabel,
    SendUndeliveredMailForm,
    UndeliveredBalancePreview,
  },
  props: {
    date: {type: String, default: () => new Date().toISOString().slice(0, 10)},
    responsibleEmail: {type: String, default: 'ALL'},
    analystEmail: {type: String, default: 'ALL'},
    reconciledClient: {type: Number, default: 0},
    forwardOptionId: {type: Number, default: 0},
  },
  mixins: [security],
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    columns() {
      return [
        {
          hidden: this.isReadOnly,
          command: [{name: 'edit'}],
          title: '&nbsp;',
          width: 60,
        },
        {
          title: ' ',
          width: '61px',
          attributes: {
            style: 'text-align: center;',
          },
          template: item => {
            return `<a title="Preview undelivered email"
                       data-id="${item.id}"
                       data-clientid="${item.clientId}"
                       data-forwardoptionid="${item.forwardOptionId}"
                       data-reportdate="${item.reportDate}"
                       href="javascript:;" class="previewUndeliveredMail fa fa-binoculars"></a>`
          },
        },
        {
          title: ' ',
          width: '61px',
          attributes: {
            style: 'text-align: center;',
          },
          template: item => {
            return `<a title="Sending undelivered email"
                       data-id="${item.id}"
                       data-clientid="${item.clientId}"
                       data-forwardoptionid="${item.forwardOptionId}"
                       data-reportdate="${item.reportDate}"
                       href="javascript:;" class="sendUndeliveredMail fa fa-paper-plane"></a>`
          },
        },
        {
          title: 'Email Id',
          field: 'id',
          width: 82,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Current State',
          field: 'status',
          width: 140,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: (() =>
            function(container, options) {
              if (options.model.status == 'Sent')
                return readOnlyEditor.call(this, container, options)

              return jsonDropDownEditor(
                'status',
                'status',
                '/api/report/sendingList/status',
                {},
                false,
              ).call(this, container, options)
            })(this),
        },
        {
          title: 'Client',
          field: 'client',
          width: 200,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Forward Option',
          field: 'forwardOptionId',
          width: 100,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.readOnlyEditor,
          template: '#=forwardOption#',
        },
        {
          title: 'Comments',
          field: 'comments',
          attributes: {style: 'text-align: left;'},
          template:
            '#= comments && comments.length > 40 ? comments.substring(0, 40) + "..." : (comments || "") #',
          width: 382,
        },
        {
          title: 'Report Date',
          field: 'reportDate',
          width: 100,
          format: '{0:yyyy-MM-dd}',
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Message Name',
          field: 'messageId',
          width: 140,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.messageEditor,
          template: '#=messageName#',
        },
        {
          title: 'From',
          field: 'from',
          width: 200,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.fromEditor,
          template: '#=from#',
        },
        {
          title: 'To',
          field: 'to',
          width: 200,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.toEditor,
          template: '#=to#',
        },
        {
          title: 'Email Signature',
          field: 'emailSignature',
          width: 282,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
          editor: this.emailSignatureEditor,
        },
        {
          title: 'Sending Date',
          field: 'sendingDate',
          width: 180,
          format: '{0:yyyy-MM-dd HH:mm:ss}',
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Sending User',
          field: 'sendingUser',
          width: 182,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          command: [{name: 'destroy', visible: dataItem => dataItem.status !== 'Sent'}],
          width: 60,
          attributes: {style: 'text-align: center;'},
          hidden: this.isReadOnly,
        },
        {},
      ].filter(c => c.editable !== false)
    },
  },
  mounted() {
    document.body.addEventListener('click', this.linkClick, false)
    const grid = this.$refs.sendingListGrid.kendoWidget()
    grid.element
      .kendoTooltip({
        show: function() {
          if (this.content.text().length > 40) {
            this.content.parent().css('visibility', 'visible')
          }
        },
        hide: function() {
          this.content.parent().css('visibility', 'hidden')
        },
        filter: 'td:nth-child(8)',
        position: 'top',
        content: function(e) {
          const dataItem = grid.dataItem(e.target.closest('tr'))
          const content = dataItem.comments
          return content
        },
      })
      .data('kendoTooltip')
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.linkClick)
  },
  methods: {
    colorize: function(e) {
      setTimeout(() => {
        const rows = e.sender.tbody.children()
        for (let j = 0; j < rows.length; j++) {
          const row = $(rows[j])
          const dataItem = getDataItem(row)
          const status = dataItem.get('status')
          row.addClass(status)
        }
      }, 100)
    },
    detailInit(e) {
      const {id, status} = e.data
      kendo
        .jQuery('<div/>')
        .appendTo(e.detailCell)
        .kendoGrid({
          editable: 'inline',
          cancel: this.colorize,
          dataBound: this.colorize,
          dataSource: {
            type: 'aspnetmvc-ajax',
            transport: {
              read: {
                type: 'GET',
                url: '/api/report/sendingList/details',
                data: () => ({id}),
                dataType: 'json',
              },
              update: {
                type: 'PUT',
                url: '/api/report/sendingList/details',
                data: () => ({id}),
                dataType: 'json',
              },
              destroy: {
                type: 'DELETE',
                url: '/api/report/sendingList/details',
                data: () => ({id}),
                dataType: 'json',
              },
            },
            schema: {
              data: 'data',
              total: 'total',
              model: {
                id: 'id',
                fields: {
                  status: {type: 'string', editable: true},
                  showTransactions: {type: 'bool', editable: status !== 'Sent'},
                  ccyPair: {type: 'string', editable: false},
                  forwardOptionId: {type: 'number', editable: false},
                  forwardOption: {type: 'string', editable: false},
                  emailSignature: {type: 'string', editable: true},
                },
              },
            },
            pageSize: 10,
            serverPaging: true,
            error: function(err) {
              notification('Error: ' + err, 'error')
            },
          },
          scrollable: false,
          sortable: false,
          pageable: true,
          columns: [
            {
              title: 'Current State',
              field: 'status',
              width: 140,
              headerAttributes: {style: 'text-align: center;'},
              attributes: {style: 'text-align: left;'},
              editor: jsonDropDownEditor(
                'status',
                'status',
                '/api/report/sendingList/status',
                {},
                false,
              ),
            },
            checkboxColumn(
              'showTransactions',
              {
                title: 'Show Transactions',
                width: 82,
              },
              this.isReadOnly,
            ),
            {
              title: 'Forward Option',
              field: 'forwardOption',
              width: 100,
              headerAttributes: {style: 'text-align: center;'},
              attributes: {style: 'text-align: left;'},
            },
            {
              title: 'CcyPair',
              field: 'ccyPair',
              width: 100,
              headerAttributes: {style: 'text-align: center;'},
              attributes: {style: 'text-align: left;'},
            },
            {
              command: [{name: 'destroy', visible: dataItem => dataItem.status !== 'Sent'}],
              width: 82,
              attributes: {style: 'text-align: center;'},
              hidden: this.isReadOnly,
            },
            {},
          ],
        })
    },
    messageEditor(container, options) {
      if (options.model.status == 'Sent') {
        var element = $('<span></span>').text(options.model.messageName)
        element.appendTo(container)
        return
      }

      const clientId = +options.model.clientId
      return jsonDropDownEditor('sendingMessageId', 'sendingMessageName', '', {
        dataSource: {
          transport: {
            read: {
              url: '/api/report/sendingList/getUndeliveredBalanceSendingMessages',
              data: {clientId: clientId},
              contentType: 'application/json',
              type: 'GET',
            },
          },
        },
      }).call(this, container, options)
    },
    fromEditor(container, options) {
      if (options.model.status == 'Sent') return readOnlyEditor.call(this, container, options)

      let input = $(
        '<input class="k-textbox" id="from"  name="' + options.field + '" maxlength="82" />',
      )
      input.appendTo(container)
      return
    },
    toEditor(container, options) {
      if (options.model.status == 'Sent') return readOnlyEditor.call(this, container, options)

      let input = $(
        '<input class="k-textbox" id="to"  name="' + options.field + '" maxlength="82" />',
      )
      input.appendTo(container)
      return
    },
    emailSignatureEditor(container, options) {
      if (options.model.status == 'Sent') {
        var element = $('<span></span>').text(options.model.emailSignature)
        element.appendTo(container)
        return
      }

      return jsonDropDownEditor('email', 'email', '', {
        dataSource: {
          transport: {
            read: {
              url: '/api/report/ExposureReport/getAnalystEmails',
              contentType: 'application/json',
              type: 'GET',
            },
          },
        },
      }).call(this, container, options)
    },
    linkClick({target}) {
      if (/sendUndeliveredMail/.test(target.className)) {
        this.openUndeliveredMailForm(target)
      } else if (/previewUndeliveredMail/.test(target.className)) {
        this.openUndeliveredBalancePreview(target)
      }
    },
    openUndeliveredMailForm(target) {
      const sendingListId = +target.dataset.id
      const clientId = +target.dataset.clientid
      const ccyPairId = -1
      const optionForwardId = +target.dataset.forwardoptionid
      const reportDate = target.dataset.reportdate
      this.formParams.sendingListId = sendingListId
      this.formParams.clientId = clientId
      this.formParams.ccyPairId = ccyPairId
      this.formParams.optionForwardId = optionForwardId
      this.formParams.asOfDate = new Date(reportDate).toISOString().slice(0, 10)
      this.formParams.reconciledClient = this.reconciledClient

      this.undeliveredMailFormOpen = true
    },
    onSuccessUndeliveredMailForm() {
      this.dataSource.read()
    },
    openUndeliveredBalancePreview(target) {
      const sendingListId = +target.dataset.id
      const reportDate = target.dataset.reportdate
      const clientId = +target.dataset.clientid
      const optionForwardId = +target.dataset.forwardoptionid

      this.undeliveredBalancePreviewParams = {
        sendingListId: sendingListId,
        asOfDate: new Date(reportDate).toISOString().slice(0, 10),
        clientId: clientId,
        responsibleEmail: 'ALL',
        analystEmail: 'ALL',
        reconciledClient: this.reconciledClient,
        url: location.origin,
        manualSettings: {
          optionValue: optionForwardId,
          ccyPairId: -1,
          showTransactionsPanel: true,
          operations: this.selectedOperations,
        },
      }

      this.undeliveredBalancePreviewVisible = !this.undeliveredBalancePreviewVisible
    },
  },
  data() {
    return {
      dataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverFiltering: true,
        serverSorting: true,
        transport: {
          read: {
            type: 'GET',
            url: '/api/report/sendingList',
            data: () => ({
              clientId: this.clientId,
              asOfDate: this.date,
              responsibleEmail: this.responsibleEmail,
              analystEmail: this.analystEmail,
              reconciledClient: this.reconciledClient,
              showOnlyUnsent: this.showOnlyUnsent,
              forwardOptionId: this.forwardOptionId,
            }),
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/report/sendingList',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          destroy: {
            type: 'DELETE',
            url: '/api/report/sendingList',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
        },
        requestEnd: e => {
          if (e.type != 'read') {
            this.$emit('changed')
          }
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'id',
            fields: {
              id: {type: 'number', editable: false},
              status: {type: 'string', editable: true},
              client: {type: 'string', editable: false},
              forwardOptionId: {type: 'number', editable: false},
              forwardOption: {type: 'string', editable: true},
              comments: {type: 'string', editable: true},
              reportDate: {type: 'date', editable: false},
              messageId: {type: 'number', editable: true},
              messageName: {type: 'string', editable: true},
              from: {type: 'string', editable: true},
              to: {type: 'string', editable: true},
              sendingDate: {type: 'date', editable: false},
              sendingUser: {type: 'string', editable: false},
            },
          },
        },
      }),
      showOnlyUnsent: false,
      undeliveredMailFormOpen: false,
      selectedOperations: null,
      undeliveredBalancePreviewVisible: false,
      undeliveredBalancePreviewParams: {},
      formParams: {
        title: 'Send Undelivered Balance Report.',
        clientId: 0,
        ccyPairId: -1,
        optionForwardId: 0,
      },
    }
  },
  watch: {
    date: function() {
      this.dataSource.read()
    },
    analystEmail: function() {
      this.dataSource.read()
    },
    responsibleEmail: function() {
      this.dataSource.read()
    },
    reconciledClient: function() {
      this.dataSource.read()
    },
    showOnlyUnsent: function() {
      this.dataSource.read()
    },
    forwardOptionId: function() {
      this.dataSource.read()
    },
  },
}
</script>
